import { useCallback, useEffect, useState } from 'react';
import { Video } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { embedUrlWithAutoplay, isUploadedVideoUrl, resolveVideoEmbedUrl } from '@/lib/videoEmbed';
import { getPublicQuoteStrings, type PublicQuoteLocale } from '@/lib/publicQuoteLocale';

const storageKey = (slug: string) => `intro-video-seen-${slug}`;

interface VideoIntroModalProps {
  slug: string;
  videoUrl: string | null | undefined;
  quoteTitle: string;
  isPrintMode: boolean;
  locale: PublicQuoteLocale;
}

export default function VideoIntroModal({
  slug,
  videoUrl,
  quoteTitle,
  isPrintMode,
  locale,
}: VideoIntroModalProps) {
  const t = getPublicQuoteStrings(locale).videoIntro;
  const [open, setOpen] = useState(false);
  const [floatVisible, setFloatVisible] = useState(false);

  const trimmed = videoUrl?.trim() || '';

  useEffect(() => {
    if (isPrintMode || !trimmed) {
      setOpen(false);
      setFloatVisible(false);
      return;
    }
    setFloatVisible(true);
    try {
      if (!localStorage.getItem(storageKey(slug))) {
        setOpen(true);
      }
    } catch {
      setOpen(true);
    }
  }, [slug, trimmed, isPrintMode]);

  const handleClose = useCallback(() => {
    try {
      localStorage.setItem(storageKey(slug), '1');
    } catch {
      /* ignore */
    }
    setOpen(false);
  }, [slug]);

  const openReplay = useCallback(() => setOpen(true), []);

  const embedSrc = trimmed ? resolveVideoEmbedUrl(trimmed) : null;
  const uploadAbsolute = trimmed && isUploadedVideoUrl(trimmed) ? api.resolveUploadUrl(trimmed) : null;
  const iframeSrc = embedSrc ? embedUrlWithAutoplay(embedSrc) : null;

  if (!trimmed || isPrintMode) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : handleClose())}>
        <DialogContent className="max-w-[min(96vw,900px)] p-0 gap-0 overflow-hidden sm:max-w-[min(96vw,900px)]">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <DialogTitle className="text-left pr-8">{t.title}</DialogTitle>
            <p className="text-sm text-muted-foreground font-normal line-clamp-2">{quoteTitle}</p>
          </DialogHeader>

          <div className="bg-black/90 px-4 py-4">
            {iframeSrc ? (
              <iframe
                src={iframeSrc}
                className="w-full aspect-video rounded-md border-0 bg-black"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                title={t.iframeTitle}
              />
            ) : uploadAbsolute ? (
              <video
                src={uploadAbsolute}
                className="w-full max-h-[min(70vh,520px)] rounded-md bg-black"
                controls
                playsInline
              />
            ) : (
              <div className="aspect-video flex items-center justify-center rounded-md bg-muted text-sm text-muted-foreground p-6 text-center">
                <a
                  href={trimmed.startsWith('http') ? trimmed : api.resolveUploadUrl(trimmed)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline break-all"
                >
                  {t.openInTab}
                </a>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t sm:justify-center">
            <Button type="button" size="lg" className="w-full sm:w-auto" onClick={handleClose}>
              {t.viewQuote}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {floatVisible && (
        <button
          type="button"
          onClick={openReplay}
          className="fixed bottom-24 right-6 z-[90] flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-2 ring-background transition hover:bg-primary/90 print:hidden"
          title={t.replayTitle}
          aria-label={t.replayAria}
        >
          <Video className="h-6 w-6" />
        </button>
      )}
    </>
  );
}
